var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ResponseType } from "@microsoft/microsoft-graph-client";
import { arrayBufferToBase64DataUri, generateInitialsAvatarDataUri, } from "../utils/imageToBase64";
// Max base64 length before we fall back to the initials avatar.
// ~200KB base64 ≈ ~150KB raw image, which is already very large for a profile photo.
const MAX_BASE64_LENGTH = 200000;
const PREFERRED_PHOTO_PATH = "/me/photos/120x120/$value";
const FALLBACK_PHOTO_PATH = "/me/photo/$value";
/**
 * Fetches the signed-in user's profile photo from Graph and returns it
 * as a base64 data URI (data:image/jpeg;base64,...).
 *
 * WHY BASE64?
 * Email clients (Outlook, Gmail, Apple Mail) block externally-hosted images
 * by default. Embedding the photo as a data URI bypasses this completely —
 * the image data is part of the email body, not an external request.
 *
 * FALLBACK CHAIN:
 * 1. GET /me/photo/$value → convert to base64 data URI
 * 2. If 404 (no photo set) or photo too large → SVG initials avatar data URI
 * 3. If displayName unavailable → generic person icon SVG
 *
 * @param client - Authenticated Graph client
 * @param displayName - Used to generate the initials fallback avatar
 */
export function fetchProfilePhotoAsDataUri(client, displayName) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const { buffer, source } = yield fetchPhotoBufferWithClient(client);
            const dataUri = arrayBufferToBase64DataUri(buffer, "image/jpeg");
            console.info(`[profilePhoto] Graph SDK photo fetch succeeded from ${source}: ${buffer.byteLength} bytes, ${dataUri.length} data URI chars`);
            if (dataUri.length > MAX_BASE64_LENGTH) {
                console.warn(`[profilePhoto] Photo too large (${dataUri.length} chars), using initials fallback`);
                return generateInitialsAvatarDataUri(displayName || "?");
            }
            return dataUri;
        }
        catch (err) {
            // 404: user has no profile photo — expected and handled gracefully
            // Other errors: log and fall back rather than crash the signature
            const status = err.statusCode;
            console.warn(`[profilePhoto] Graph SDK photo fetch failed${status ? ` with HTTP ${status}` : ""}; using initials fallback`, err);
            return generateInitialsAvatarDataUri(displayName || "?");
        }
    });
}
/**
 * Fetches the profile photo using a raw access token (without a Graph client).
 * Used by the event-based handler (commands.ts) where the Graph client SDK
 * may not be initialized. Makes a direct fetch() call instead.
 */
export function fetchProfilePhotoAsDataUriWithToken(accessToken, displayName) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const { buffer, source } = yield fetchPhotoBufferWithToken(accessToken);
            const dataUri = arrayBufferToBase64DataUri(buffer, "image/jpeg");
            console.info(`[profilePhoto] Raw photo fetch succeeded from ${source}: ${buffer.byteLength} bytes, ${dataUri.length} data URI chars`);
            if (dataUri.length > MAX_BASE64_LENGTH) {
                console.warn(`[profilePhoto] Raw photo fetch exceeded limit (${dataUri.length} chars); using initials fallback`);
                return generateInitialsAvatarDataUri(displayName || "?");
            }
            return dataUri;
        }
        catch (err) {
            console.warn("[profilePhoto] Fetch error:", err);
            return generateInitialsAvatarDataUri(displayName || "?");
        }
    });
}
function fetchPhotoBufferWithClient(client) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const buffer = yield client
                .api(PREFERRED_PHOTO_PATH)
                .responseType(ResponseType.ARRAYBUFFER)
                .get();
            return { buffer, source: PREFERRED_PHOTO_PATH };
        }
        catch (err) {
            const status = err.statusCode;
            if (status && status !== 404) {
                console.warn(`[profilePhoto] Sized photo fetch failed with HTTP ${status}; falling back to full photo`);
            }
        }
        const buffer = yield client
            .api(FALLBACK_PHOTO_PATH)
            .responseType(ResponseType.ARRAYBUFFER)
            .get();
        return { buffer, source: FALLBACK_PHOTO_PATH };
    });
}
function fetchPhotoBufferWithToken(accessToken) {
    return __awaiter(this, void 0, void 0, function* () {
        const sizedResponse = yield fetch(`https://graph.microsoft.com/v1.0${PREFERRED_PHOTO_PATH}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (sizedResponse.ok) {
            return {
                buffer: yield sizedResponse.arrayBuffer(),
                source: PREFERRED_PHOTO_PATH,
            };
        }
        if (sizedResponse.status !== 404) {
            console.warn(`[profilePhoto] Raw sized photo fetch returned HTTP ${sizedResponse.status}; falling back to full photo`);
        }
        const fullResponse = yield fetch(`https://graph.microsoft.com/v1.0${FALLBACK_PHOTO_PATH}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!fullResponse.ok) {
            throw new Error(`Raw photo fetch returned HTTP ${fullResponse.status}`);
        }
        return {
            buffer: yield fullResponse.arrayBuffer(),
            source: FALLBACK_PHOTO_PATH,
        };
    });
}
